import type { CompanyBalanceOrder } from './companyBalanceApi';
import { resolveOrderRealization } from './companyBalanceRealization';
import { localeForLanguage } from '../../../utils/numberFormatting';

export interface CompanyBalanceCsvLabels {
  headers: {
    orderNumber: string;
    customer: string;
    orderStatus: string;
    plannedDate: string;
    realization: string;
    settlementStatus: string;
    currentRevenue: string;
    currentCost: string;
    currentMargin: string;
    currentMarginPercent: string;
    recognizedRevenue: string;
    recognizedCost: string;
    recognizedMargin: string;
  };
  orderStatus: (status: string) => string;
  settlementStatus: (status: CompanyBalanceOrder['settlementStatus']) => string;
}

function csvCell(value: string | number): string {
  return `"${String(value).replace(/"/g, '""')}"`;
}

function decimal(value: number, language: string): string {
  return new Intl.NumberFormat(localeForLanguage(language), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(Number.isFinite(value) ? value : 0);
}

export function buildCompanyBalanceOrdersCsv(
  orders: CompanyBalanceOrder[],
  language: string,
  labels: CompanyBalanceCsvLabels,
): string {
  const headers = [
    labels.headers.orderNumber,
    labels.headers.customer,
    labels.headers.orderStatus,
    labels.headers.plannedDate,
    labels.headers.realization,
    labels.headers.settlementStatus,
    labels.headers.currentRevenue,
    labels.headers.currentCost,
    labels.headers.currentMargin,
    labels.headers.currentMarginPercent,
    labels.headers.recognizedRevenue,
    labels.headers.recognizedCost,
    labels.headers.recognizedMargin,
  ];

  const rows = orders.map((order) => {
    const realization = resolveOrderRealization(order.evidence);
    return [
      order.displayOrderNumber,
      order.customer?.name || '',
      labels.orderStatus(order.status),
      order.plannedDate?.slice(0, 10) || '',
      realization.total > 0 ? `${realization.percent}%` : '',
      labels.settlementStatus(order.settlementStatus),
      decimal(order.current.revenue, language),
      decimal(order.current.totalCost, language),
      decimal(order.current.margin, language),
      decimal(order.current.marginPercent, language),
      decimal(order.periodRecognized.revenue, language),
      decimal(order.periodRecognized.totalCost, language),
      decimal(order.periodRecognized.margin, language),
    ];
  });

  return [headers, ...rows]
    .map((row) => row.map(csvCell).join(';'))
    .join('\r\n');
}

export function companyBalanceOrdersFilename(from: string, to: string): string {
  return `bilans-firmy-zamowienia_${from}_${to}.csv`;
}
